/** Dipakai hanya kalau respons tidak mengirim angkanya. Default backend: 120 dan 300. */
export const OTP_RESEND_FALLBACK_SECONDS = 120
export const OTP_TTL_FALLBACK_SECONDS = 300

const USERNAME_MISSING = 'Username tidak ditemukan.'
const NO_EMAIL = 'Akun tidak memiliki email yang dapat dikirimi OTP.'
const RESEND_WAIT = 'Kode OTP baru dapat dikirim setelah jeda selesai.'
const TOO_MANY = 'Terlalu banyak permintaan. Silakan coba lagi nanti.'
const OTP_MISMATCH = 'Kode OTP tidak sesuai.'
const OTP_EXHAUSTED = 'Percobaan OTP sudah habis. Silakan minta kode baru.'
const OTP_INVALID = 'Kode OTP tidak valid atau sudah kedaluwarsa.'
const TOKEN_EXPIRED = 'Token reset tidak valid atau sudah kedaluwarsa.'
const TOKEN_INVALID = 'Token reset tidak valid.'
const PASSWORD_SHORT = 'Password minimal 8 karakter.'
const PASSWORD_MISMATCH = 'Konfirmasi password tidak sesuai.'

export interface OtpWindow {
  maskedEmail: string
  expiresIn: number
  resendIn: number
}

export type PasswordResetFailure =
  | { kind: 'username'; message: string; username?: string }
  | { kind: 'otp-window'; message: string; window: OtpWindow }
  | { kind: 'rate-limit'; message: string }
  | { kind: 'otp-mismatch'; message: string }
  | { kind: 'otp-reset'; message: string }
  | { kind: 'restart'; message: string }
  | {
      kind: 'password'
      message: string
      password?: string
      confirmation?: string
    }
  | { kind: 'stay'; message: string }

export function readSeconds(value: unknown, fallback: number) {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : Number.NaN

  if (!Number.isFinite(parsed) || parsed < 0) return fallback
  return Math.floor(parsed)
}

export function readOtpWindow(data: unknown): OtpWindow {
  const record =
    data && typeof data === 'object' ? (data as Record<string, unknown>) : {}

  return {
    maskedEmail:
      typeof record.masked_email === 'string' ? record.masked_email : '',
    expiresIn: readSeconds(record.expires_in, OTP_TTL_FALLBACK_SECONDS),
    resendIn: readSeconds(
      record.resend_available_in,
      OTP_RESEND_FALLBACK_SECONDS
    )
  }
}

export function secondsRemaining(endsAt: number | null, now: number) {
  if (endsAt == null) return null
  return Math.max(0, Math.ceil((endsAt - now) / 1000))
}

export function passwordChecks(password: string, confirmation: string) {
  const hasLetter = /[A-Za-z]/.test(password)
  const hasNumber = /\d/.test(password)

  return {
    length: password.length >= 8,
    letterAndNumber: hasLetter && hasNumber,
    uppercase: /[A-Z]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: confirmation.length > 0 && password === confirmation
  }
}

export function passwordRuleMessage(password: string) {
  const checks = passwordChecks(password, '')
  if (!checks.length) return 'Password minimal 8 karakter.'
  if (!checks.letterAndNumber)
    return 'Password harus mengandung huruf dan angka.'
  if (!checks.uppercase) return 'Password harus mengandung huruf besar.'
  if (!checks.special) return 'Password harus mengandung karakter khusus.'
  return null
}

export function formatClock(totalSeconds: number) {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function normalizeFieldErrors(errors: unknown) {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors))
    return null

  const normalized: Record<string, string> = {}
  for (const [key, value] of Object.entries(errors)) {
    const messages = Array.isArray(value) ? value : [value]
    const text = messages.find(
      (item): item is string => typeof item === 'string' && item.length > 0
    )
    if (text) normalized[key] = text
  }

  return Object.keys(normalized).length > 0 ? normalized : null
}

function readCount(value: unknown) {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : Number.NaN

  if (!Number.isFinite(parsed) || parsed < 0) return null
  return Math.floor(parsed)
}

export function classifyPasswordResetError(
  error: unknown
): PasswordResetFailure {
  const record =
    error && typeof error === 'object' ? (error as Record<string, unknown>) : {}
  const status = typeof record.status === 'number' ? record.status : 500
  const message =
    typeof record.message === 'string' && record.message.trim()
      ? record.message.trim()
      : 'Terjadi kesalahan. Silakan coba lagi.'
  const errors = normalizeFieldErrors(record.errors)
  const usernameError = errors?.username
  const otpError = errors?.otp
  const passwordError = errors?.password
  const confirmationError = errors?.password_confirmation

  if (status === 404 || message === USERNAME_MISSING) {
    return { kind: 'username', message, username: usernameError }
  }

  if (message === NO_EMAIL) {
    return { kind: 'username', message, username: usernameError }
  }

  if (status === 429 && message === RESEND_WAIT) {
    return {
      kind: 'otp-window',
      message,
      window: readOtpWindow(record.data)
    }
  }

  if (status === 429 || message === TOO_MANY) {
    return { kind: 'rate-limit', message }
  }

  if (message === OTP_MISMATCH) {
    const data =
      record.data && typeof record.data === 'object'
        ? (record.data as Record<string, unknown>)
        : null
    const remaining = readCount(data?.attempts_remaining)
    const detail =
      remaining == null ? message : `${message} Sisa percobaan: ${remaining}.`

    return { kind: 'otp-mismatch', message: otpError ?? detail }
  }

  if (message === OTP_EXHAUSTED || message === OTP_INVALID) {
    return { kind: 'otp-reset', message: otpError ?? message }
  }

  if (message === TOKEN_EXPIRED || message === TOKEN_INVALID) {
    return { kind: 'restart', message }
  }

  if (
    message === PASSWORD_SHORT ||
    message === PASSWORD_MISMATCH ||
    passwordError ||
    confirmationError
  ) {
    return {
      kind: 'password',
      message,
      password:
        passwordError ?? (message === PASSWORD_SHORT ? message : undefined),
      confirmation:
        confirmationError ??
        (message === PASSWORD_MISMATCH ? message : undefined)
    }
  }

  if (usernameError) {
    return { kind: 'username', message, username: usernameError }
  }

  if (otpError) {
    return { kind: 'otp-mismatch', message: otpError }
  }

  return { kind: 'stay', message }
}
